import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mountain, Sprout, Wind, Radar, Microscope, Loader2 } from "lucide-react";
import GeologyPanel from "@/components/ground/GeologyPanel";
import SoilPanel, { baseSuitability } from "@/components/ground/SoilPanel";
import GeomorphPanel from "@/components/ground/GeomorphPanel";
import GeophysicsPanel from "@/components/ground/GeophysicsPanel";
import { SaveFinding } from "@/components/ground/shared";
import DisciplineQuiz from "@/components/ground/DisciplineQuiz";

const ICONS = { geology: Mountain, soil: Sprout, geomorphology: Wind, geophysics: Radar };

function summaryFor(id, d) {
    if (id === "geology") return `Geology — ${d.name}: ${d.geology.era}. Dominant minerals: ${d.geology.minerals.slice(0, 3).map((m) => `${m.name} ${m.pct}%`).join(", ")}.`;
    if (id === "soil") return `Soil — ${d.name}: pH ${d.soil.ph}, perchlorate ${d.soil.perchlorate_wt_pct} wt%, water ${d.soil.water_content_pct} wt%, raw plant suitability ${baseSuitability(d.soil)}/100.`;
    if (id === "geomorphology") return `Geomorphology — ${d.name}: landforms ${d.geomorphology.landforms.map((l) => l.name).join(", ")}; slope ${d.slope_deg}°.`;
    return `Geophysics — ${d.name}: crust ${d.geophysics.crust_thickness_km} km, gravity ${d.geophysics.gravity_anomaly_mgal} mGal, radar reflector ~${d.geophysics.radar_reflector_depth_m} m (ε′ ${d.geophysics.dielectric}).`;
}

function DisciplineTabs({ disciplines, active, onPick, results }) {
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {disciplines.map((d, i) => {
                const Icon = ICONS[d.id];
                const on = active === d.id;
                return (
                    <motion.button key={d.id} onClick={() => onPick(d.id)} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                        className={`text-left rounded-xl border p-4 transition-colors ${on ? "border-ares-orange/60 bg-ares-orange/10" : "border-ares-border bg-ares-dark/90 hover:border-ares-orange/30"}`}
                        data-testid={`discipline-tab-${d.id}`}>
                        <Icon className={`w-5 h-5 mb-2 ${on ? "text-ares-orange" : "text-ares-muted"}`} />
                        <p className="font-heading font-semibold">{d.name}</p>
                        <p className="text-xs text-ares-muted mt-0.5">{d.tagline}</p>
                        {results[d.id] && (
                            <span className={`inline-block mt-2 font-mono text-[10px] rounded-full px-2 py-0.5 border ${results[d.id].best === results[d.id].total ? "text-ares-emerald border-ares-emerald/40 bg-ares-emerald/10" : "text-ares-amber border-ares-amber/40 bg-ares-amber/10"}`} data-testid={`discipline-quiz-badge-${d.id}`}>
                                Quiz {results[d.id].best}/{results[d.id].total}
                            </span>
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
}

function DisciplineIntro({ d }) {
    return (
        <div className="rounded-xl border border-ares-border bg-ares-dark/90 p-5 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="discipline-intro">
            <div className="lg:col-span-2">
                <p className="text-ares-bright leading-relaxed" data-testid="discipline-definition">{d.definition}</p>
                <p className="text-sm text-ares-muted mt-2 leading-relaxed">{d.mars_focus}</p>
            </div>
            <div className="space-y-3">
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-1.5">Methods</p>
                    <div className="flex flex-wrap gap-1.5">{d.methods.map((m) => <span key={m} className="text-[11px] rounded-full bg-ares-panel border border-ares-border px-2 py-0.5">{m}</span>)}</div>
                </div>
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ares-muted mb-1.5">Instruments</p>
                    <div className="flex flex-wrap gap-1.5">{d.instruments.map((m) => <span key={m} className="text-[11px] font-mono rounded-full bg-ares-cyan/10 text-ares-cyan border border-ares-cyan/30 px-2 py-0.5">{m}</span>)}</div>
                </div>
            </div>
        </div>
    );
}

export default function GroundSciences() {
    const [meta, setMeta] = useState(null);
    const [regions, setRegions] = useState([]);
    const [regionId, setRegionId] = useState("utopia");
    const [disc, setDisc] = useState("geology");
    const [data, setData] = useState(null);
    const [quizProg, setQuizProg] = useState({ results: {}, mastered: 0, total_disciplines: 4 });

    const loadQuiz = () => api.get("/ground-quiz/progress").then(({ data }) => setQuizProg(data)).catch(() => {});
    useEffect(() => {
        loadQuiz();
        api.get("/ground-sciences").then(({ data }) => setMeta(data));
        api.get("/regions").then(({ data }) => setRegions(data));
    }, []);
    useEffect(() => {
        setData(null);
        api.get(`/ground-sciences/${regionId}`).then(({ data }) => setData(data));
    }, [regionId]);

    const d = meta?.disciplines.find((x) => x.id === disc);
    return (
        <div className="space-y-5" data-testid="ground-sciences-module">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-semibold flex items-center gap-2"><Microscope className="w-6 h-6 text-ares-orange" /> Ground Sciences Lab</h2>
                    <p className="text-sm text-ares-muted mt-1">Four disciplines, one region — read the ground from surface dust to the core.</p>
                    <div className="flex items-center gap-2 mt-2" data-testid="ground-quiz-mastery">
                        <div className="w-32 h-1.5 rounded-full bg-ares-void overflow-hidden">
                            <div className="h-full bg-ares-emerald transition-[width] duration-500" style={{ width: `${(quizProg.mastered / quizProg.total_disciplines) * 100}%` }} />
                        </div>
                        <span className="font-mono text-[11px] text-ares-muted" data-testid="ground-quiz-mastery-count">{quizProg.mastered}/{quizProg.total_disciplines} disciplines mastered</span>
                    </div>
                </div>
                <Select value={regionId} onValueChange={setRegionId}>
                    <SelectTrigger className="w-56 bg-ares-panel border-ares-border" data-testid="ground-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ares-panel border-ares-border">
                        {regions.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`ground-region-${r.id}`}>{r.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {meta && <DisciplineTabs disciplines={meta.disciplines} active={disc} onPick={setDisc} results={quizProg.results} />}
            {d && <DisciplineIntro d={d} />}
            {!data || !meta ? (
                <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-ares-orange" /></div>
            ) : (
                <motion.div key={`${disc}-${regionId}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                    {disc === "geology" && <GeologyPanel data={data} />}
                    {disc === "soil" && <SoilPanel data={data} />}
                    {disc === "geomorphology" && <GeomorphPanel data={data} />}
                    {disc === "geophysics" && <GeophysicsPanel data={data} interior={meta.interior} />}
                    <div className="rounded-xl border border-ares-border bg-ares-dark/90 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        <p className="text-xs text-ares-muted flex-1" data-testid="ground-summary-text">{summaryFor(disc, data)}</p>
                        <div className="sm:w-80"><SaveFinding text={summaryFor(disc, data)} featureType={disc} /></div>
                    </div>
                    <DisciplineQuiz key={disc} discipline={d} progress={quizProg.results[disc]} onDone={loadQuiz} />
                </motion.div>
            )}
        </div>
    );
}
